import React, { useEffect, useMemo, useRef, useState } from "react";
import FormCreator from "../../components/Forms/FormCreator";
import Buttons from "../../components/ui/button/Buttons";
import { InvoiceApprovalData } from "./Data/InvoiceApproval.data";
import { InvoiceApprovalConfig } from "./Data/InvoiceApprovalConfig";
import FinalMolGrid from "../../components/FinalMolGrid/FinalMolGrid";
import { GridColumnType } from "../../components/FinalMolGrid/libs/data/gridColumnType";
import GridCheckbox from "../../components/FinalMolGrid/ui/checkbox/GridCheckbox";
import CenterModel from "../../components/ui/centerModel/CenterModel";
import { useCheckManualInvoiceNumberMutation, useInvoiceApprovalTaskListMutation, useUpdateInvoiceApprovalStatusMutation } from "../../app/services/CertificateGrouping/certificateGroupingApi";
import { useLazyGetDropDownsForProductsAndServicesQuery } from "../../app/services/productAndService/productAndServiceApi";
import { setAllDropDownField } from "./DropdownUtils/DropdownUtils";
import ToastService from "../../services/toastService/ToastService";
import { useAddAuditLogForSGBPApplcation } from "../../utils/AuditLogQuery/addAuditLog";


const parseApplications = (row) => {
  const ids = (row?.sgbpApplicationId ?? "").toString().split(",").map((s) => s.trim());
  const statuses = (row?.sgbpApplicationStatusId ?? "").toString().split(",").map((s) => s.trim());

  return ids
    .map((id, index) => ({
      applicationId: Number(id),
      statusId: statuses[index] ? Number(statuses[index]) : null,
    }))
    .filter((item) => item.applicationId); // empty / NaN / 0 remove
};

const getUniqueApplications = (rows) => {
  const map = new Map();
  rows.forEach((row) => {
    parseApplications(row).forEach((app) => {
      if (!map.has(app.applicationId)) {
        map.set(app.applicationId, app);
      }
    });
  });
  return Array.from(map.values());
};

const InvoiceApprovalList = () => {
  const invoiceApprovalRef = useRef(null);
  const molGridRef = useRef(null);
  const [formData, setFormData] = useState(InvoiceApprovalData);
  const [selectedRows, setSelectedRows] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [showCertDetailsModal, setShowCertDetailsModal] = useState(false);
  const [certDetailsData, setCertDetailsData] = useState([]);
  const [paginationRequest, setPaginationRequest] = useState({
    pageNo: 1,
    pageSize: 10,
    CompanyName: null,
    totalCount: 0,
    ApproverId: null,
    CertificateNumber: null,
  });

  const [productsAndServicesDropdown, { data: productsAndServicesDropDownsData },] = useLazyGetDropDownsForProductsAndServicesQuery();
  const [invoiceApprovalTaskList, { isSuccess: isSuccessinvoiceList, isLoading: isLoadingInvoiceList, data: batchInvoiceData },] = useInvoiceApprovalTaskListMutation();
  const [updateInvoiceApprovalStatus, { isSuccess: isSuccessUpdateInvoiceList, isLoading: isLoadingUpdateInvoiceList, data: updatedInvoiceData },] = useUpdateInvoiceApprovalStatusMutation();
  const [checkManualInvoiceNumber, { isSuccess: isSuccessCheckManualInvoiceNumber, isLoading: isLoadingCheckManualInvoiceNumber, data: checkManualInvoiceData },] = useCheckManualInvoiceNumberMutation();
  const { addAuditLog } = useAddAuditLogForSGBPApplcation();

  useEffect(() => {
    invoiceApprovalTaskList({});
    productsAndServicesDropdown();
  }, []);

  useEffect(() => {
    if (!isLoadingUpdateInvoiceList && isSuccessUpdateInvoiceList && updatedInvoiceData) {
      ToastService.success("Invoice approval status updated successfully.");
      invoiceApprovalTaskList({});
      setSelectedRows([]);
    }
  }, [isLoadingUpdateInvoiceList, isSuccessUpdateInvoiceList, updatedInvoiceData]);

  useEffect(() => {
    setAllDropDownField(productsAndServicesDropDownsData);
  }, [productsAndServicesDropDownsData]);

  const handleSearch = () => {
    const formValues = invoiceApprovalRef.current.getFormData();
    const requestPayload = {
      pageNo: 1,
      pageSize: 10,
      CompanyName: formValues?.searchByCompanyName || null,
      ApproverId: formValues?.searchByApprover?.value || formValues?.searchByApprover || null,
      CertificateNumber: formValues?.searchByCertificateNumber || null,
    };
    setPaginationRequest(requestPayload);
    setSelectedRows([]);
    invoiceApprovalTaskList(requestPayload);
  };

  const handleClear = () => {
    const updatedFormData = { ...formData };
    updatedFormData.initialState = {
      searchByCompanyName: null,
      searchByApprover: null,
      searchByCertificateNumber: null,
    };

    const request = {
      pageNo: 1,
      CompanyName: null,
      ApproverId: null,
      CertificateNumber: null,
    };
    setPaginationRequest((prev) => ({ ...prev, ...request }));
    setFormData(updatedFormData);
    setSelectedRows([]);
    invoiceApprovalTaskList(request);
  };

  useEffect(() => {
    if (isLoadingInvoiceList || !isSuccessinvoiceList) return;
    const updatedData = (batchInvoiceData?.itemList ?? []).map((row) => ({
      ...row,
      id: row.invoiceApprovalId || row.id,
      manualInvoiceNumber: row.manualInvoiceNumber || "-",
      certificateDetails: row.certificateDetails ?? [],
    }));
    setPaginationRequest(prev => ({ ...prev, totalCount: batchInvoiceData?.totalCount }));
    setDataSource(updatedData);
    // setSelectedRows([]);
  }, [isLoadingInvoiceList, isSuccessinvoiceList, batchInvoiceData]);

  const handlePageChange = ({ pageNumber, pageSize }) => {
    const updatedPaginationRequest = { ...paginationRequest, pageNo: pageNumber, pageSize: pageSize };
    setPaginationRequest(updatedPaginationRequest);
    invoiceApprovalTaskList(updatedPaginationRequest);
  };

  const selectableRows = useMemo(() => {
    return dataSource.filter((row) => row.status === "Pending");
  }, [dataSource]);

  const handleRowSelection = (selectedRow) => {
    if (selectedRow?.status !== "Pending") {
      return;
    }

    setSelectedRows((previousRows) => {
      const exists = previousRows.some((row) => row.id === selectedRow.id);

      if (exists) {
        return previousRows.filter((row) => row.id !== selectedRow.id);
      }

      return [...previousRows, selectedRow];
    });
  };

  const handleCheckboxChange = (event, rowData) => {
    event.stopPropagation();

    if (rowData?.status !== "Pending") {
      return;
    }

    handleRowSelection(rowData);
  };

  const openCertDetailsModal = (event, rowData) => {
    event?.stopPropagation();

    const certNumbers = (rowData?.certNumber || "").split(",").map((c) => c.trim()).filter(Boolean);
    const itemCodes = (rowData?.itemCode || "").split(",").map((c) => c.trim()).filter(Boolean);
    const pairedDetails = certNumbers.map((certNumber, index) => ({
      certNumber,
      itemCode: itemCodes[index] || "-",
    }));

    setCertDetailsData(pairedDetails);
    setShowCertDetailsModal(true);
  };

  const closeCertDetailsModal = () => {
    setShowCertDetailsModal(false);
    setCertDetailsData([]);
  };

  const invoiceApprovalGridConfig = useMemo(() => {
    return {
      ...InvoiceApprovalConfig,
      columns: InvoiceApprovalConfig.columns.map((column) => {
        if (column.fieldName === "select") {
          return {
            ...column, colType: GridColumnType.CUSTOM,
            renderCustomCol: (rowData) => {
              const isPending = rowData?.status === "Pending";
              const isSelected = selectedRows.some((row) => row.id === rowData.id);

              return (
                <GridCheckbox
                  type="checkbox"
                  checked={isSelected}
                  dataField="select"
                  disabled={!isPending}
                  onChange={(event) => handleCheckboxChange(event, rowData)}
                />
              );
            },
          };
        }

        if (column.fieldName === "certNumber") {
          return {
            ...column, colType: GridColumnType.CUSTOM,
            renderCustomCol: (rowData) => (
              <span
                className="text-success custom-text-break text-decoration-underline"
                style={{ cursor: "pointer", fontSize: "16px" }}
                onClick={(event) => openCertDetailsModal(event, rowData)}
              >
                {rowData.certNumber}
              </span>
            ),
          };
        }

        return column;
      }),
    };
  }, [selectedRows]);

  const handleHeaderSelect = (_dataSourceFromGrid, event) => {
    const isChecked = event?.target?.checked;
    if (isChecked) {
      setSelectedRows(selectableRows);
    } else {
      setSelectedRows([]);
    }
  };

  // const req = {
  //     SGBPSApplicationId: SGBPSApplicationId || 0,
  //     AuditLogDescription: AuditLogDescription || "",
  //     byWho: authData?.userName || "",
  //     TriggredTimeStatusId: TriggredTimeStatusId || null,
  // };

  const logAuditForRows = async (rows, isApprove) => {
    const applications = getUniqueApplications(rows);
    const results = await Promise.allSettled(
      applications.map((app) =>
        addAuditLog({
          SGBPSApplicationId: app.applicationId,
          TriggredTimeStatusId: app.statusId,
          AuditLogDescription: isApprove ? "Invoice approved" : "Invoice rejected",
        })
      )
    );

    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length > 0) {
      console.error("Some audit logs failed", failed);
    }
  };

  const submitApproval = async (isApprove) => {
    await updateInvoiceApprovalStatus({
      InvoiceApprovalList: selectedRows.map((row) => ({
        InvoiceId: row.id,
        IsApprove: isApprove,
      })),
    }).unwrap();
    try {
      await logAuditForRows(selectedRows, isApprove);
    } catch (e) {
      console.error("Audit log error", e);
    }
  };

  const handleApprovalAction = async (isApprove) => {
    if (selectedRows.length === 0) {
      return;
    }
    setActionInProgress(isApprove ? "approve" : "reject");
    try {
      const rowsWithManualInvoice = selectedRows.filter((row) => row?.manualInvoiceNumber && row.manualInvoiceNumber !== "-" && row.manualInvoiceNumber.trim() !== "");
      if (rowsWithManualInvoice.length === 0) {
        await submitApproval(isApprove);
        return;
      }
      for (const row of rowsWithManualInvoice) {
        const response = await checkManualInvoiceNumber({ ManualInvoiceNumber: row.manualInvoiceNumber.trim() }).unwrap();
        if (!response?.isValid) {
          ToastService.error(response?.message);
          return;
        }
      }
       await submitApproval(isApprove);
    } catch (error) {
      ToastService.error("The manual invoice number could not be found in Xero. Please verify the invoice number and try again.");
    }
  };

  const handleApprove = () => {
    handleApprovalAction(true);
  };

  const handleReject = () => {
    handleApprovalAction(false);
  };

  return (
    <div>
      <div className="crud-requirement-list">
        <div className="corporate-member-list">
          <div className="sgbp-category">
            <div className="row status-input pr-0 col-9">
              {productsAndServicesDropDownsData && (
                <FormCreator ref={invoiceApprovalRef} {...formData} />
              )}
            </div>
            <div className="d-flex align-items-end mb-3 mt-auto gap-3">
              <Buttons
                buttonText="Search"
                buttonTypeClassName="search-btn"
                onClick={handleSearch}
              />
              <Buttons
                buttonText="Clear"
                buttonTypeClassName="btn btn-secondary"
                onClick={handleClear}
              />
            </div>
          </div>

          <div className="member-list invoice-approve-list">
            <FinalMolGrid
              ref={molGridRef}
              configuration={invoiceApprovalGridConfig}
              dataSource={dataSource}
              allowPagination={true}
              pagination={paginationRequest}
              onPageChange={handlePageChange}
              rowIdKey="id"
              selectedRows={selectedRows}
              onHandleRowSelect={handleRowSelection}
              onHeaderCheckboxChange={handleHeaderSelect}
            />
          </div>

          <div className="d-flex justify-content-between mt-5">
            <Buttons buttonText="Back" buttonTypeClassName="black-btn" />
            <div className="d-flex gap-3">
              <Buttons
                buttonText="Reject"
                buttonTypeClassName="danger-btn"
                onClick={handleReject}
                isDisable={selectedRows.length === 0 || (isLoadingUpdateInvoiceList || isLoadingCheckManualInvoiceNumber)}
                isLoading={actionInProgress === "reject" && (isLoadingUpdateInvoiceList || isLoadingCheckManualInvoiceNumber)}
              />
              <Buttons
                buttonText="Approve"
                buttonTypeClassName="success-btn"
                onClick={handleApprove}
                isDisable={selectedRows.length === 0 || (isLoadingUpdateInvoiceList || isLoadingCheckManualInvoiceNumber)}
                isLoading={actionInProgress === "approve" && (isLoadingUpdateInvoiceList || isLoadingCheckManualInvoiceNumber)}
              />
            </div>
          </div>
        </div>
      </div>

      <CenterModel
        show={showCertDetailsModal}
        handleClose={closeCertDetailsModal}
        modelTitle="Certificate Details"
        modelWidth="w-45"
      >
        <div className="common-details-table-wrapper">
          <table className="table common-details-table">
            <thead>
              <tr>
                <th>Certificates</th>
                <th>Item Code</th>
              </tr>
            </thead>

            <tbody>
              {certDetailsData.length > 0 ? (
                certDetailsData.map((cert, index) => (
                  <tr key={`${cert.certNumber}-${index}`}>
                    <td>{cert.certNumber}</td>
                    <td>{cert.itemCode}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={2} className="common-details-table-empty">
                    No certificate details available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CenterModel>
    </div>
  );
};

export default InvoiceApprovalList;